import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BarChart3, ShoppingCart, Euro, Gavel, RotateCcw, Users, ChevronLeft, ChevronRight, FileDown, FileSpreadsheet, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import EmailStatusCard from "../components/EmailStatusCard";
import ChatAlertsCard from "../components/ChatAlertsCard";
import SuspensionsCard from "../components/SuspensionsCard";
import AdminUsersTable from "../components/AdminUsersTable";
import AdminPaymentsTable from "../components/AdminPaymentsTable";
import AdminCommissionDues from "../components/AdminCommissionDues";
import AdminOrdersTable from "../components/AdminOrdersTable";
import UnpaidAlertsCard from "../components/UnpaidAlertsCard";
import AdminVerificationQueue from "../components/AdminVerificationQueue";

const TABS = [["overview", "Riepilogo"], ["orders", "Ordini"], ["users", "Utenti"], ["verifications", "Verifiche"], ["payments", "Pagamenti"], ["commissions", "Commissioni"]];

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const STATUS = { open: "In asta", awarded: "Aggiudicati", paid: "Pagati", delivered: "Consegnati", expired: "Scaduti", cancelled: "Annullati", reassigning: "In riassegnazione" };
const METHOD = { card: "Carta", bank_transfer: "Bonifico", paypal: "PayPal", credit: "Credito piattaforma", satispay: "Satispay (legacy mock)", heylight: "HeyLight (legacy mock)" };
const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Kpi({ icon: Icon, label, value, sub, testId, tone = "" }) {
  return (
    <div className={`ga-card p-5 ${tone}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</div>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className="mt-2 font-display text-3xl font-extrabold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-600">{sub}</div>}
    </div>
  );
}

function Breakdown({ title, items, labels, testId }) {
  const entries = Object.entries(items || {});
  return (
    <div className="ga-card p-5" data-testid={testId}>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">{title}</div>
      {entries.length === 0 && <div className="text-sm text-slate-500">Nessun dato.</div>}
      <div className="space-y-1.5">
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm"><span className="text-slate-700">{labels[k] || k}</span><span className="font-mono font-bold">{v}</span></div>
        ))}
      </div>
    </div>
  );
}

export default function AdminOverview() {
  const now = new Date();
  const qs = new URLSearchParams(window.location.search);
  const qy = Number(qs.get("year")), qm = Number(qs.get("month"));
  const [ym, setYm] = useState(qy && qm >= 1 && qm <= 12 ? { y: qy, m: qm } : { y: now.getFullYear(), m: now.getMonth() + 1 });
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(qs.get("tab") || "overview");
  const { search } = useLocation();
  useEffect(() => {
    const fromLink = new URLSearchParams(search).get("tab");
    if (fromLink) setTab(fromLink);
  }, [search]);
  useEffect(() => {
    setData(null);
    api.get(`/admin/stats?year=${ym.y}&month=${ym.m}`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [ym]);
  const shift = (d) => setYm(({ y, m }) => { const t = new Date(y, m - 1 + d, 1); return { y: t.getFullYear(), m: t.getMonth() + 1 }; });
  const isCurrent = ym.y === now.getFullYear() && ym.m === now.getMonth() + 1;
  const [sending, setSending] = useState(false);
  const sendReport = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/monthly-report/send?year=${ym.y}&month=${ym.m}&force=true`);
      toast.success(`Report inviato a ${data.sent} admin${data.attached ? " (PDF allegato)" : " (link al PDF)"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio fallito"); }
    finally { setSending(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-overview-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5" /> Pannello owner</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Riepilogo piattaforma</h1>
          <div className="mt-1 flex gap-3 text-sm">
            <Link to="/admin/contestazioni" className="text-slate-600 underline" data-testid="link-admin-disputes">Contestazioni →</Link>
            <Link to="/ristorante?as=buyer" className="text-slate-400 underline" data-testid="link-buyer-mode">Modalità acquirente (test)</Link>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="month-picker">
          <button onClick={() => shift(-1)} className="p-2 rounded-full border border-slate-300 hover:bg-slate-50" data-testid="month-prev" aria-label="Mese precedente"><ChevronLeft className="w-4 h-4" /></button>
          <div className="font-display font-bold text-slate-900 min-w-[160px] text-center" data-testid="month-label">{MONTHS[ym.m - 1]} {ym.y}</div>
          <button onClick={() => shift(1)} disabled={isCurrent} className="p-2 rounded-full border border-slate-300 hover:bg-slate-50 disabled:opacity-40" data-testid="month-next" aria-label="Mese successivo"><ChevronRight className="w-4 h-4" /></button>
          <a href={`${API}/admin/stats.pdf?year=${ym.y}&month=${ym.m}`} className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3 py-2 text-xs font-semibold hover:bg-slate-800" data-testid="export-pdf-button"><FileDown className="w-3.5 h-3.5" /> PDF</a>
          <a href={`${API}/admin/stats.csv?year=${ym.y}&month=${ym.m}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-2 text-xs font-semibold hover:bg-slate-50" data-testid="export-csv-button"><FileSpreadsheet className="w-3.5 h-3.5" /> CSV</a>
          <button onClick={sendReport} disabled={sending} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-2 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50" data-testid="send-report-button" title="Invia subito via email il PDF di questo mese agli admin"><Mail className="w-3.5 h-3.5" /> {sending ? "Invio..." : "Invia via email"}</button>
        </div>
      </div>

      <div className="mt-6 flex gap-1 border-b border-slate-200" data-testid="admin-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid={`admin-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "orders" && <div className="mt-6"><AdminOrdersTable /></div>}
      {tab === "users" && <div className="mt-6"><AdminUsersTable /></div>}
      {tab === "verifications" && <div className="mt-6"><AdminVerificationQueue /></div>}
      {tab === "payments" && <div className="mt-6"><AdminPaymentsTable /></div>}
      {tab === "commissions" && <div className="mt-6"><AdminCommissionDues /></div>}
      {tab === "overview" && data === null && <div className="mt-8 text-sm text-slate-500">Caricamento...</div>}
      {tab === "overview" && data?.error && <div className="mt-8 text-sm text-red-700">Impossibile caricare le statistiche.</div>}
      {tab === "overview" && <>
        <div className="mt-6"><EmailStatusCard /></div>
        <div className="mt-4"><ChatAlertsCard /></div>
        <div className="mt-4"><UnpaidAlertsCard /></div>
        <div className="mt-4"><SuspensionsCard /></div>
      </>}
      {tab === "overview" && data && !data.error && (
        <>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={ShoppingCart} label="Ordini creati" value={data.orders.created} sub={`${data.orders.paid} pagati · ${data.orders.delivered} consegnati · ${data.orders.express} express`} testId="kpi-orders" />
            <Kpi icon={Euro} label="Volume pagato" value={eur(data.volume.gmv_eur)} sub={`Commissioni ${eur(data.volume.commission_eur)} · medio ${eur(data.volume.avg_order_eur)}`} testId="kpi-volume" tone="border-emerald-300" />
            <Kpi icon={Gavel} label="Contestazioni" value={data.disputes.opened} sub={`${data.disputes.open} aperte · ${data.disputes.accepted} accolte · ${data.disputes.rejected} respinte · ${data.disputes.rate_pct}% delle consegne`} testId="kpi-disputes" tone={data.disputes.open > 0 ? "border-red-300" : ""} />
            <Kpi icon={RotateCcw} label="Rimborsi" value={eur(data.refunds.total_eur)} sub={`${data.refunds.count} rimborsi · ${data.refunds.pending} in lavorazione`} testId="kpi-refunds" />
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Breakdown title="Ordini per stato" items={data.orders.by_status} labels={STATUS} testId="breakdown-status" />
            <Breakdown title="Pagamenti per metodo" items={data.volume.by_method} labels={METHOD} testId="breakdown-method" />
            <div className="ga-card p-5" data-testid="kpi-users">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Utenti</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-700">Nuovi nel mese</span><span className="font-mono font-bold">{data.users.new}</span></div>
                <div className="flex justify-between"><span className="text-slate-700">di cui acquirenti</span><span className="font-mono">{data.users.new_buyers}</span></div>
                <div className="flex justify-between"><span className="text-slate-700">di cui fornitori</span><span className="font-mono">{data.users.new_suppliers}</span></div>
                <div className="flex justify-between border-t border-slate-200 pt-1.5"><span className="text-slate-700">Totale iscritti</span><span className="font-mono font-bold">{data.users.total}</span></div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
